import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cx from 'classnames';

import { actionTypes } from '../../store/actions/imageModal';
import metaActions from '../../store/actions/meta';
import { checkIsNewDate, checkIsAnotherSender, decodeString } from '../../utils/';
import { usersRoles } from '../../constants';

import DateSeparator from '../DateSeparator';
import ExpertCard from '../ExpertCard';
import ExpertTyping from '../ExpertTyping';
import Message from './Message';
import TermsOfUse from './TermsOfUse';

import s from './index.module.css';
import {
  getHasMessageHistory,
  getIsEmex,
  getPhoneNumber,
  getSenderId,
  getExpertsOnlineStatus
} from '../../store/selectors';

const MessagesList = ({ onSelectLocation, isWidgetPath, chatWindowRef, actualMessages = [], typingExperts = [] }) => {
  const dispatch = useDispatch();
  const phoneNumber = useSelector(getPhoneNumber);
  const hasHistory = useSelector(getHasMessageHistory);
  const isEmex = useSelector(getIsEmex);
  const userSenderId = useSelector(getSenderId);
  const expertsOnlineStatus = useSelector(getExpertsOnlineStatus);
  const open = useCallback((payload) => dispatch({ type: actionTypes.OPEN, payload }), [dispatch]);
  const userMessages = actualMessages.filter((message) => message.sender && message.sender.type_id === usersRoles.BUYER);

  const lastMessage = actualMessages.length > 0 ? actualMessages[actualMessages.length - 1] : null;
  const lastMessageSenderId = lastMessage && lastMessage.sender && lastMessage.sender.sender_id;
  const typingExpert = typingExperts[0];
  const isAnotherSender = typingExpert && lastMessageSenderId ? lastMessageSenderId !== typingExpert.senderId : false;
  const isAfterCard = lastMessage && lastMessage.content && lastMessage.content.type === 'expertCard';
  const isShowTermsOfUse = userMessages.length === 0;

  useEffect(() => {
    const hasOrderMessage = actualMessages.some((item) => item.sender.type_id === 3);

    if (phoneNumber && hasOrderMessage) {
      dispatch(metaActions.fetchOrders(phoneNumber));
    }
  }, [phoneNumber]);

  return (
    <div className={s.messagesList}>
      {actualMessages
        .sort((a, b) => {
          if (a.timestamp > b.timestamp) {
            return 1;
          }
          if (a.timestamp < b.timestamp) {
            return -1;
          }

          return a.message_id - b.message_id;
        })
        .map((message, index) => {
          const { timestamp, content, sender, message_id, is_read } = message;
          const isExpertMessage = userSenderId !== sender.senderId;
          const isOnline = expertsOnlineStatus && 
            expertsOnlineStatus.filter(t => t.senderId === sender.sender_id)[0]?.isOnline;
            
          const isAnotherSender = checkIsAnotherSender(actualMessages, message, index);
          const isShowTermsOfUse = userMessages.length > 0 && index === 1;

          return (
            <div key={`${index}-${message_id}-${timestamp}`}>
              {checkIsNewDate(actualMessages, message, index) && hasHistory && <DateSeparator timestamp={timestamp} />}
              {content.type === 'expertCard' && Boolean(content.card) ? (
                <ExpertCard className={s.expertCard} card={content.card} />
              ) : (
                <Message
                  senderType={sender ? sender.type_id : 3}
                  userName={sender && sender.name}
                  avatarUrl={sender && sender.picture_path}
                  time={timestamp}
                  type={content.type}
                  offers={content.offers}
                  mapOffers={content.mapOffers}
                  location={content.location}
                  order={content.order}
                  messageContent={content.text}
                  authOptions={content.authOptions}
                  amount={content.amount}
                  valid={content.valid}
                  showGroupedRows={content.showGroupedRows}
                  detailCard={content.detailCard}
                  attachments={content.attachments}
                  onSelectLocation={onSelectLocation}
                  onSelectImage={(index) => open({ message_id, attachmentIndex: index })}
                  isAnotherSender={isAnotherSender}
                  isExpertMessage={isExpertMessage}
                  isWidgetPath={isWidgetPath}
                  messageId={message_id}
                  score={content.score}
                  rateExpertId={content.expertId}
                  showFeedback={content.showRateComment}
                  chatWindowRef={chatWindowRef}
                  visible={content.visible}
                  isReaded={is_read}
                  isExpertOnline={isOnline}
                  isEmex={isEmex}
                >
                  {Boolean(content.text) &&
                    <p className={cx(s.textMessage,
                      { [s.emexTextMessage]: isEmex })}>
                      {decodeString(content.text)}
                    </p>}
                </Message>
              )}
              {isShowTermsOfUse && <TermsOfUse />}
            </div>
          );
        })}
      {typingExperts.length > 0 &&
        typingExperts.map((expert) => (
          <ExpertTyping
            key={expert.senderId}
            isTyping={expert.isTyping}
            typingExpert={expert}
            isAnotherSender={isAnotherSender || isAfterCard || !lastMessage}
          />
        ))}
      {isShowTermsOfUse && <TermsOfUse />}
    </div>
  );
};

export default MessagesList;
